import React, { useState, useRef, useEffect } from 'react';
import { UserCircleIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';
import StaffDateAvailabilityForm from '../service/StaffDateAvailabilityForm';
import UserProfileForm from './UserProfileForm';
import BlockTimeSlotForm from './BlockTimeSlotForm';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCompactMode } from '../../contexts/CompactModeContext';
import { USER_ROLES, ERROR_MESSAGES } from '../../constants';

const UserDropdown = ({ userEmail, userRole, userName, currentUserId, onProfileUpdate }) => {
  const { t } = useLanguage();
  const { isCompactMode, toggleCompactMode } = useCompactMode();
  const [isOpen, setIsOpen] = useState(false);
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showBlockTimeSlotForm, setShowBlockTimeSlotForm] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      console.log('Attempting to sign out...');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Sign out error:', error);
        alert(`${t('messages.error.signOutError')}: ${error.message}`);
      } else {
        console.log('Sign out successful, reloading page...');
        // Clear any local storage if needed
        localStorage.clear();
        sessionStorage.clear();
        
        // Redirect to login page instead of just reloading
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Unexpected error during sign out:', err);
      alert(t('messages.error.unexpectedSignOutError'));
    }
  };

  const handleSetAvailability = () => {
    setShowAvailabilityForm(true);
    setIsOpen(false);
  };

  const handleUpdateProfile = () => {
    setShowProfileForm(true);
    setIsOpen(false);
  };

  const handleBlockTimeSlot = () => {
    setShowBlockTimeSlotForm(true);
    setIsOpen(false);
  };

  const handleProfileClose = () => {
    setShowProfileForm(false);
    // Call the callback to refresh customer data if provided
    if (onProfileUpdate) {
      onProfileUpdate();
    }
  };

  const displayName = userName || userEmail?.split('@')[0] || 'User';

  return (
    <>
      <div className="relative transition-all duration-300 ease-in-out" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-2 transition-all duration-300 ease-in-out"
        >
          <UserCircleIcon className="h-8 w-8 text-gray-600 transition-all duration-300 ease-in-out" />
          <div className="text-left transition-all duration-300 ease-in-out">
            <p className="text-sm font-medium transition-all duration-300 ease-in-out">{displayName}</p>
            <p className="text-xs text-gray-500 capitalize transition-all duration-300 ease-in-out">({(() => {
              try {
                // Add check to ensure userRole exists and is not empty
                if (!userRole || userRole.trim() === '') {
                  return 'User';
                }
                
                const roleTranslation = t(`roles.${userRole}`);
                // Check if translation was found (not equal to the key itself)
                if (roleTranslation && roleTranslation !== `roles.${userRole}`) {
                  return roleTranslation;
                }
                // Fallback to capitalized userRole
                return userRole.charAt(0).toUpperCase() + userRole.slice(1);
              } catch (error) {
                console.warn('Error translating user role:', error);
                return userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1) : 'User';
              }
            })()})</p>
          </div>
          <ChevronDownIcon className={`h-4 w-4 transition-all duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 transition-all duration-300 ease-in-out">
            <div className="py-1 transition-all duration-300 ease-in-out">
              {/* Compact Mode Toggle */}
              <button
                onClick={toggleCompactMode}
                className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
              >
                <div className="flex items-center">
                  <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                  {t('userDropdown.compactMode')}
                </div>
                <div className="flex items-center">
                  <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                    isCompactMode ? 'bg-blue-600' : 'bg-gray-300'
                  }`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                      isCompactMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </div>
                </div>
              </button>
              
              {(userRole === USER_ROLES.STAFF || userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.MANAGER) && (
                <>
                  <button
                    onClick={handleSetAvailability}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                  >
                    <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('userDropdown.setAvailability')}
                  </button>
                  <button
                    onClick={handleBlockTimeSlot}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
                  >
                    <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                    </svg>
                    {t('userDropdown.blockTimeSlot')}
                  </button>
                </>
              )}
              <button
                onClick={handleUpdateProfile}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
              >
                <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {t('userDropdown.myProfile')}
              </button>
              
              {/* Language Switcher */}
              <LanguageSwitcher isInDropdown={true} />
              
              <div className="border-t border-gray-100 my-1"></div>
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 hover:text-red-900 transition-colors duration-200"
              >
                <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {t('userDropdown.logout')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Availability Form Modal */}
      {showAvailabilityForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <StaffDateAvailabilityForm
              staffId={currentUserId}
              onClose={() => setShowAvailabilityForm(false)}
            />
          </div>
        </div>
      )}

      {/* Profile Form Modal */}
      {showProfileForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <UserProfileForm
              userId={currentUserId}
              onClose={handleProfileClose}
            />
          </div>
        </div>
      )}

      {/* Block Time Slot Form Modal */}
      {showBlockTimeSlotForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <BlockTimeSlotForm
              staffId={currentUserId}
              userRole={userRole}
              onClose={() => setShowBlockTimeSlotForm(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default UserDropdown;